import { useState, useEffect, useRef, memo, useCallback } from "react";
import type { CSSProperties } from "react";
import { AlertCircle } from "lucide-react";

interface ImageWithSkeletonProps {
  src: string;
  alt: string;
  containerClassName?: string;
  containerStyle?: CSSProperties;
  className?: string;
  skeletonClassName?: string;
  onClick?: () => void;
  /** Priority loading - skip lazy loading for above-the-fold images */
  priority?: boolean;
  /** Unique key to force image reload even with same src */
  instanceKey?: string;
  /** Intrinsic width hint for the browser to reserve space (CLS optimization) */
  width?: number;
  /** Intrinsic height hint for the browser to reserve space (CLS optimization) */
  height?: number;
  /** Responsive srcSet for automatic resolution selection */
  srcSet?: string;
  /** Sizes hint for srcSet (e.g. "(max-width: 768px) 100vw, 600px") */
  sizes?: string;
  /** Base64 data URL for blur placeholder (LQIP) — shown instead of grey skeleton */
  blurDataUrl?: string;
  /** Optional CORS mode for images that need canvas-based analysis */
  crossOrigin?: "anonymous" | "use-credentials" | "";
  /** Called before the image is faded in */
  onImageLoad?: (image: HTMLImageElement) => void;
}

/**
 * ImageWithSkeleton Component
 * 
 * Unified lazy loading + skeleton pattern:
 * 1. Shows skeleton placeholder immediately
 * 2. Uses native lazy loading (unless priority)
 * 3. Fades in image when loaded
 * 4. Handles errors gracefully
 * 5. Resets state when src or instanceKey changes
 */
const ImageWithSkeleton = memo(({
  src,
  alt,
  containerClassName = "",
  containerStyle,
  className = "",
  skeletonClassName = "",
  onClick,
  priority = false,
  instanceKey,
  width,
  height,
  srcSet,
  sizes,
  blurDataUrl,
  crossOrigin,
  onImageLoad,
}: ImageWithSkeletonProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);
  
  // Store callbacks in refs to avoid stale closures
  const isLoadedRef = useRef(isLoaded);
  isLoadedRef.current = isLoaded;

  // Reset state when src or instanceKey changes
  useEffect(() => {
    setIsLoaded(false);
    setHasError(false);
  }, [src, instanceKey]);

  // Check if image is already cached/complete after mount and on src change
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    if (priority) {
      img.setAttribute("fetchpriority", "high");
    } else {
      img.removeAttribute("fetchpriority");
    }
  }, [priority, src, instanceKey]);

  // Check if image is already cached/complete after mount and on src change
  useEffect(() => {
    const img = imgRef.current;
    if (!img) return;

    // Use a small timeout to ensure the DOM has updated
    const checkComplete = () => {
      if (img.complete && img.naturalWidth > 0 && !isLoadedRef.current) {
        onImageLoad?.(img);
        setIsLoaded(true);
      }
    };

    // Check immediately
    checkComplete();
    
    // Also check after a short delay for cached images
    const timer = setTimeout(checkComplete, 50);
    
    return () => clearTimeout(timer);
  }, [src, instanceKey, onImageLoad]);

  const handleLoad = useCallback(() => {
    // Double-check the image is valid before setting loaded
    if (imgRef.current?.naturalWidth && imgRef.current?.naturalWidth > 0) {
      onImageLoad?.(imgRef.current);
      setIsLoaded(true);
    }
  }, [onImageLoad]);

  const handleError = useCallback(() => {
    setHasError(true);
    setIsLoaded(true);
  }, []);

  // Base container with relative positioning for overlay
  const containerClass = `relative overflow-hidden ${containerClassName}`.trim();
  
  // Dynamic aspect-ratio from width/height props (zero layout shift)
  const hasDynamicDimensions = width && height && !(width === 800 && height === 600);
  const dynamicStyle = hasDynamicDimensions
    ? { aspectRatio: `${width} / ${height}` }
    : undefined;
  
  // Skeleton dimensions should match image dimensions
  const skeletonBase = skeletonClassName || (dynamicStyle ? "w-full" : "w-full aspect-video");

  // Unique key for forcing re-render when same src but different task
  const imageKey = instanceKey ? `${src}-${instanceKey}` : src;

  return (
    <div className={containerClass} style={containerStyle}>
      {/* Skeleton placeholder - visible until image loads */}
      {!isLoaded && !hasError && (
        <div
          className={`${skeletonBase} flex items-center justify-center`}
          style={dynamicStyle}
          aria-hidden={isLoaded}
        >
          {blurDataUrl ? (
            <div
              className="absolute inset-0 rounded-xl"
              style={{
                backgroundImage: `url(${blurDataUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                filter: 'blur(20px)',
                transform: 'scale(1.1)',
              }}
            />
          ) : (
            <div className="absolute inset-0 rounded-xl bg-muted/20 animate-pulse" />
          )}
        </div>
      )}

      {/* Error state */}
      {hasError && (
        <div className={`${skeletonBase} flex items-center justify-center bg-muted/50 rounded-xl border border-border`}>
          <div className="flex flex-col items-center gap-2 text-muted-foreground p-4">
            <AlertCircle className="w-6 h-6 md:w-8 md:h-8 text-destructive/60" />
            <span className="text-xs font-medium text-center">Nie udało się załadować obrazu</span>
          </div>
        </div>
      )}

      {/* Actual image - always render but hide visually until loaded */}
      {!hasError && (
        <img
          ref={imgRef}
          key={imageKey}
          src={src}
          alt={alt}
          width={width}
          height={height}
          srcSet={srcSet}
          sizes={sizes}
          crossOrigin={crossOrigin}
          loading={priority ? "eager" : "lazy"}
          decoding="async"
          onLoad={handleLoad}
          onError={handleError}
          onClick={onClick}
          className={`${className} transition-opacity duration-300 ${isLoaded ? 'opacity-100' : 'opacity-0 absolute inset-0'}`}
          style={{ 
            // Use visibility instead of display:none to allow natural loading
            visibility: isLoaded ? 'visible' : 'hidden',
            position: isLoaded ? 'relative' : 'absolute',
          }}
        />
      )}
    </div>
  );
});

ImageWithSkeleton.displayName = 'ImageWithSkeleton';

export default ImageWithSkeleton;
